import React, { useState } from 'react';
import {
    Modal,
    Paper,
    Typography,
    Box,
    Button,
    Avatar,
    ListItemText,
    Divider,
    Chip,
    TextField
} from '@mui/material';

const NotifyReviewerModal = ({
                                 open,
                                 onClose,
                                 onNotify,
                                 eligibleReviewers = [],
                                 reviewHistory = []
                             }) => {
    const [selectedReviewer, setSelectedReviewer] = useState('');
    const [note, setNote] = useState('');

    const handleSubmit = () => {
        if (!selectedReviewer) return;

        onNotify(selectedReviewer, note); // Pass the note
        setSelectedReviewer('');
        setNote('');
        onClose();
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Paper
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 600,
                    maxWidth: '95vw',
                    p: 4,
                    bgcolor: '#202020',
                    color: '#fff',
                    border: '1px solid #333',
                    borderRadius: 2
                }}
            >
                <Typography variant="h6" sx={{ color: '#12c099', mb: 2 }}>
                    Notify a Reviewer
                </Typography>

                <Typography variant="body2" sx={{ color: '#aaa', mb: 3 }}>
                    Invite one reviewer at a time. Once a reviewer finishes their feedback, others will see updated logs before submitting.
                </Typography>

                {/* === Reviewer History Section === */}
                {reviewHistory.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" sx={{ color: '#ccc', mb: 1 }}>
                            🕓 Reviewer History
                        </Typography>
                        {reviewHistory.map((r, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
                                <Avatar src={r.avatar} alt={r.name} sx={{ width: 36, height: 36 }} />
                                <Box>
                                    <Typography sx={{ color: '#fff' }}>{r.name}</Typography>
                                    <Typography variant="caption" sx={{ color: '#aaa' }}>
                                        {r.date} — {r.status}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={r.status}
                                    size="small"
                                    color={
                                        r.status === 'Approved' ? 'success' :
                                            r.status === 'Denied' ? 'error' :
                                                r.status === 'Reviewing' ? 'info' : 'warning'
                                    }
                                    sx={{ ml: 'auto' }}
                                />
                            </Box>
                        ))}
                    </Box>
                )}

                <Divider sx={{ borderColor: '#444', mb: 3 }} />

                {/* === Eligible Reviewers List === */}
                <Typography variant="subtitle2" sx={{ color: '#ccc', mb: 1 }}>
                    ✅ Available Reviewers
                </Typography>
                <Box sx={{ maxHeight: 220, overflowY: 'auto', mb: 3 }}>
                    {eligibleReviewers.length === 0 && (
                        <Typography variant="body2" sx={{ color: '#777' }}>No eligible reviewers available.</Typography>
                    )}
                    {eligibleReviewers
                        .filter(user => !reviewHistory.some(r => r.name === user.name))
                        .map((user) => (
                            <Box
                                key={user.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    mb: 2,
                                    p: 2,
                                    border: '1px solid #333',
                                    borderRadius: 2,
                                    backgroundColor: selectedReviewer === user.id ? '#2c2c2c' : 'transparent',
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: '#2a2a2a' }
                                }}
                                onClick={() => setSelectedReviewer(user.id)}
                            >
                                <Avatar src={user.avatar} alt={user.name} sx={{ width: 44, height: 44 }} />
                                <ListItemText
                                    primary={user.name}
                                    secondary={user.role || ''}
                                    primaryTypographyProps={{ sx: { color: '#fff' } }}
                                    secondaryTypographyProps={{ sx: { color: '#aaa' } }}
                                />
                                {user.status && (
                                    <Chip
                                        label={user.status}
                                        size="small"
                                        color={user.status === 'reviewing' ? 'info' : 'warning'}
                                    />
                                )}
                            </Box>
                        ))}
                </Box>

                {/* === Optional Note Field === */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ color: '#ccc', mb: 1 }}>
                        Add a Note (optional)
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="E.g. Let me know if you spot any issues in the forecast logic."
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        InputProps={{ sx: { color: '#fff' } }}
                        InputLabelProps={{ sx: { color: '#ccc' } }}
                        sx={{ backgroundColor: '#1a1a1a' }}
                    />
                </Box>

                {/* === Action Buttons === */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button variant="outlined" onClick={onClose}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!selectedReviewer}
                        onClick={handleSubmit}
                    >
                        Send Notification
                    </Button>
                </Box>
            </Paper>
        </Modal>
    );
};

export default NotifyReviewerModal;
